import axios, { type AxiosResponse } from 'axios'
import { useAuthStore } from '@/plugins/w/auth/store/auth.store'
import { isTokenHandshakeActive, markTokenHandshakeCompleted } from './token-handshake'

function isMaintenanceStatusRequest(url: string) {
	return url.includes('maintenance-status')
}

function getMaintenancePayload(response: AxiosResponse) {
	const data = response.data as { data?: Record<string, unknown> } | undefined
	return data?.data
}

function patchMaintenanceAuthenticated(response: AxiosResponse): AxiosResponse {
	const payload = getMaintenancePayload(response)
	if (!payload) return response

	payload.user_authenticated = true
	return response
}

export function setupTokenHandshakeGuards() {
	axios.interceptors.response.use(
		response => {
			const url = String(response.config?.url || '')
			if (!isMaintenanceStatusRequest(url)) return response

			const authStore = useAuthStore()
			const data = response.data?.data

			if (data?.user_authenticated === false) {
				if (authStore.isLoggedIn) {
					return patchMaintenanceAuthenticated(response)
				}

				if (isTokenHandshakeActive()) {
					return Promise.reject(new Error('Token handshake pending'))
				}
			}

			return response
		},
		error => {
			const url = String(error?.config?.url || '')
			if (isMaintenanceStatusRequest(url) && isTokenHandshakeActive()) {
				const authStore = useAuthStore()
				if (!authStore.isLoggedIn) {
					return Promise.reject(new Error('Token handshake pending'))
				}

				const fallbackResponse: AxiosResponse = error.response ?? {
					data: {
						data: {
							maintenance_mode: false,
							user_authenticated: false
						}
					},
					status: 200,
					statusText: 'OK',
					headers: {},
					config: error.config ?? {}
				}

				return Promise.resolve(patchMaintenanceAuthenticated(fallbackResponse))
			}

			if (!isTokenHandshakeActive()) return Promise.reject(error)

			const errorText = error?.response?.data?.error
			const status = error?.response?.status

			if (status === 401 && errorText === 'Unauthenticated.') {
				return Promise.reject(error)
			}

			return Promise.reject(error)
		}
	)
}

export async function completeTokenHandshakeAuth(router: { replace: (location: { name: string }) => Promise<unknown> }) {
	await router.replace({ name: 'App' })
	markTokenHandshakeCompleted()
}
